import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, CheckCircle2, ExternalLink, FileText, RotateCcw, Search, Send } from 'lucide-react'
import {
  Button,
  Checkbox,
  Chip,
  CountryPicker,
  Drawer,
  Input,
  Modal,
  PageHeaderBand,
  QueryErrorState,
  Select,
  Textarea,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { useMediaQuery } from '@/lib/hooks/useMediaQuery'
import { useCurrentUser } from '@/shell/services/auth'
import { documentUrl } from '@/shell/services/cofa-registry/utils/document-url'
import { odooProductTemplateUrl } from '@/lib/utils/odoo-url'
import { useDocumentTypes } from '../api/queries'
import {
  useArchiveOnboardingDocument,
  useCancelOnboarding,
  useHsCodeSuggestions,
  useOnboardingDetail,
  usePatchOnboarding,
  usePushOnboarding,
  useReextractOnboarding,
  useReopenOnboarding,
  useReturnOnboarding,
  useSignOnboarding,
  useSubmitOnboarding,
  useUomOptions,
  useUploadOnboardingDocument,
} from '../api/onboarding-queries'
import type { OnboardingDetail, OnboardingFields } from '../api/onboarding-types'
import { ExtractionPanel } from '../components/ExtractionPanel'
import { NextStepBar, type BarAction } from '../components/NextStepBar'
import { LookupPicker } from '../components/LookupPicker'
import { OnboardingChecklist } from '../components/OnboardingChecklist'
import { OnboardingHistory } from '../components/OnboardingHistory'
import { TiersEditor } from '../components/TiersEditor'
import { VendorsEditor } from '../components/VendorsEditor'
import { FormField, PanelCard, PdfDropZone, SaveStatusPill, SectionNav, StepSection } from '@/shell/forms/FormKit'
import { useAutosaveForm } from '@/shell/forms/autosave'
import { formatDate } from '../utils/dates'
import { cleanList, docList, sameList } from '../utils/doc-lists'
import {
  documentChips,
  jumpTo,
  ONBOARDING_SECTIONS,
  REQUIRED_FIELDS,
  sectionStatus,
  targetForChecklistKey,
  type SectionId,
} from '../utils/section-status'
import {
  COMMERCIAL_SIGN_ROLES,
  EDIT_ROLES,
  FIELD_EDIT_ROLES,
  REGULATORY_SIGN_ROLES,
  STATUS_LABEL,
  STATUS_VARIANT,
} from '../utils/onboarding-meta'

const FIELD_KEYS: (keyof OnboardingFields)[] = [
  'name', 'default_code', 'spi_branded', 'categ_id', 'categ_name', 'description_sale', 'sale_ok', 'purchase_ok',
  'uom_id', 'uom_name', 'uom_po_id', 'uom_po_name', 'pack_size', 'pack_description', 'inci_names', 'cas_numbers',
  'country_of_origin_id', 'country_of_origin_name', 'hs_code', 'is_hazmat', 'hazmat_un_number', 'hazmat_class_code',
  'hazmat_packing_group', 'hazmat_proper_shipping_name', 'manufacturer_name', 'manufacturer_address',
  'manufacturer_emergency_phone', 'vendors', 'list_price', 'price_tiers', 'notes',
]

function pickFields(d: OnboardingDetail): OnboardingFields {
  return Object.fromEntries(FIELD_KEYS.map((k) => [k, d[k]])) as unknown as OnboardingFields
}

/** New-product request: enter → checklist → regulatory + commercial sign-off → created in Odoo. */
export function OnboardingDetailPage() {
  const params = useParams({ strict: false }) as { intakeId?: string }
  const id = params.intakeId ?? null
  const { data, isLoading, isError, error, refetch } = useOnboardingDetail(id)
  if (isError) {
    return (
      <div className="p-6">
        <QueryErrorState error={error} onRetry={() => void refetch()} subject="new product request" />
      </div>
    )
  }
  if (isLoading || !data) return <div className="p-6 text-secondary">Loading…</div>
  // Keyed by id only: a background refresh (upload, document reading, another
  // user's save) must never remount the form and drop what's being typed.
  return <OnboardingDetailView key={data.id} detail={data} />
}

type Dialog =
  | { kind: 'return' }
  | { kind: 'cancel' }
  | { kind: 'reopen' }
  | null

function OnboardingDetailView({ detail }: { detail: OnboardingDetail }) {
  const navigate = useNavigate()
  const { user: me } = useCurrentUser()
  const canEditRole = useHasAnyPermission([...EDIT_ROLES])
  const canFieldEdit = useHasAnyPermission([...FIELD_EDIT_ROLES])
  // The side panel sits beside the form only on wide screens; otherwise it opens as a drawer.
  const wide = useMediaQuery('(min-width: 1600px)')
  const [panelOpen, setPanelOpen] = useState(false)
  const [signOpen, setSignOpen] = useState(false)
  const canRegulatory = useHasAnyPermission([...REGULATORY_SIGN_ROLES])
  const canCommercial = useHasAnyPermission([...COMMERCIAL_SIGN_ROLES])
  // Admins can always give the operations sign-off, even on their own submission (Chris 2026-10-11).
  const isAdmin = useHasAnyPermission(['admin'])
  const editable = detail.editable && canFieldEdit
  const s = detail.status

  const patch = usePatchOnboarding()
  const server = useMemo(() => pickFields(detail), [detail])
  const saveFields = useCallback(
    (values: Partial<OnboardingFields>) => patch.mutateAsync({ id: detail.id, values }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutateAsync is stable
    [detail.id],
  )
  const { form, set, setMany, status: saveStatus, error: saveError, unsaved: dirty } = useAutosaveForm(server, saveFields)

  const submit = useSubmitOnboarding()
  const sign = useSignOnboarding()
  const ret = useReturnOnboarding()
  const cancel = useCancelOnboarding()
  const push = usePushOnboarding()
  const reopen = useReopenOnboarding()
  const reextract = useReextractOnboarding()

  const [dialog, setDialog] = useState<Dialog>(null)
  const [dialogText, setDialogText] = useState('')
  const [signNote, setSignNote] = useState('')
  const [ackBelowCost, setAckBelowCost] = useState(false)

  const myEmail = (me?.email ?? '').toLowerCase()
  const isSubmitter = !!detail.submitted_by && detail.submitted_by.toLowerCase() === myEmail
  const isRegSigner = !!detail.regulatory_signed_by && detail.regulatory_signed_by.toLowerCase() === myEmail
  const belowCost = detail.completeness.items.some((i) => i.key === 'below_cost' && !i.ok)
  const hasSignoff = !!(detail.regulatory_signed_by || detail.commercial_signed_by)

  const fail = (title: string) => (err: unknown) => toast.danger(title, { description: (err as Error).message })

  const openDialog = (d: Dialog) => {
    setDialogText('')
    setDialog(d)
  }

  const signKind: 'regulatory' | 'commercial' | null =
    s === 'regulatory_review' && canRegulatory ? 'regulatory' : s === 'commercial_review' && canCommercial ? 'commercial' : null
  const signBlocked = isAdmin
    ? null
    : isSubmitter
    ? 'You submitted this request; someone else has to sign it off.'
    : signKind === 'commercial' && isRegSigner
      ? 'You gave the regulatory sign-off; the operations sign-off needs someone else.'
      : null

  const doSign = () => {
    if (!signKind) return
    sign.mutate(
      { id: detail.id, kind: signKind, note: signNote, acknowledgeBelowCost: ackBelowCost },
      {
        onSuccess: (res) => {
          setSignOpen(false)
          if (signKind === 'regulatory') toast.success('Regulatory sign-off recorded')
          else if (res.push?.ok) toast.success('Approved and created in Odoo')
          else toast.danger('Approved, but creating it in Odoo failed', { description: res.push?.error ?? undefined })
        },
        onError: fail('Sign-off failed'),
      },
    )
  }

  const runDialog = () => {
    if (!dialog) return
    const done = (msg: string) => () => {
      toast.success(msg)
      setDialog(null)
    }
    switch (dialog.kind) {
      case 'return':
        return ret.mutate({ id: detail.id, reason: dialogText }, { onSuccess: done('Returned to draft'), onError: fail('Return failed') })
      case 'cancel':
        return cancel.mutate({ id: detail.id, reason: dialogText }, { onSuccess: done('Request cancelled'), onError: fail('Cancel failed') })
      case 'reopen':
        return reopen.mutate({ id: detail.id, reason: dialogText }, { onSuccess: done('Reopened for correction'), onError: fail('Reopen failed') })
    }
  }

  const dialogMeta: Record<NonNullable<Dialog>['kind'], { title: string; description: string; label: string; required: boolean; action: string }> = {
    return: { title: 'Return to draft', description: 'The request goes back to whoever is completing it. Sign-offs are cleared.', label: 'What needs fixing', required: true, action: 'Return' },
    cancel: { title: 'Cancel request', description: 'The request is closed and nothing is created in Odoo.', label: 'Reason', required: true, action: 'Cancel request' },
    reopen: { title: 'Reopen for correction', description: 'Use when the push failed because of the data. It goes back to regulatory review; if the product was already created in Odoo, the next push corrects that product.', label: 'What needs fixing', required: true, action: 'Reopen' },
  }
  const meta = dialog ? dialogMeta[dialog.kind] : null
  const busy = ret.isPending || cancel.isPending || reopen.isPending

  // ── The one next action for this viewer (NextStepBar) ──
  const doSubmit = () =>
    submit.mutate(detail.id, { onSuccess: () => toast.success('Submitted for regulatory review'), onError: fail('Submit failed') })
  const doPush = () =>
    push.mutate(detail.id, {
      onSuccess: (r) => (r.ok ? toast.success('Created in Odoo') : toast.danger('Push failed', { description: r.error })),
      onError: fail('Push failed'),
    })
  let primary: BarAction | null = null
  let waiting: string | null = null
  let blocked: string | null = null
  if (s === 'draft' && canEditRole) {
    primary = { label: 'Submit for review', icon: <Send size={18} />, onClick: doSubmit, loading: submit.isPending,
      disabled: !detail.completeness.ok || dirty }
    blocked = dirty ? 'Saving your changes…'
      : detail.completeness.ok ? null : `${detail.completeness.error_count} item(s) missing; see the checklist`
  } else if (s === 'draft' || s === 'awaiting_vendor') {
    waiting = s === 'awaiting_vendor' ? 'Waiting for the vendor' : 'Being filled in by operations'
  } else if (signKind) {
    primary = { label: signKind === 'regulatory' ? 'Give regulatory sign-off' : 'Give operations sign-off',
      icon: <CheckCircle2 size={18} />, onClick: () => setSignOpen(true), disabled: signBlocked !== null || dirty }
    blocked = signBlocked ?? (dirty ? 'Saving your changes…' : null)
  } else if (s === 'regulatory_review') {
    waiting = 'Waiting for the regulatory team'
  } else if (s === 'commercial_review') {
    waiting = 'Waiting for operations sign-off'
  } else if ((s === 'approved' || s === 'push_failed') && canCommercial) {
    primary = { label: s === 'push_failed' ? 'Retry push' : 'Push to Odoo', icon: <RotateCcw size={18} />,
      onClick: doPush, loading: push.isPending }
  } else if (s === 'approved') {
    waiting = 'Approved; being created in Odoo'
  }
  const secondary: BarAction[] = []
  if (!dirty && ((s === 'regulatory_review' && (canRegulatory || isAdmin)) || (s === 'commercial_review' && canCommercial))) {
    secondary.push({ label: 'Return for changes', tone: 'secondary', onClick: () => openDialog({ kind: 'return' }) })
  }
  if (s === 'push_failed' && canCommercial) {
    secondary.push({ label: 'Reopen for correction', tone: 'secondary', onClick: () => openDialog({ kind: 'reopen' }) })
  }
  if (canEditRole && !['pushed_to_odoo', 'cancelled'].includes(s) && !Object.keys(detail.push_steps).length) {
    secondary.push({ label: 'Cancel request', tone: 'ghost', onClick: () => openDialog({ kind: 'cancel' }) })
  }

  const sections = ONBOARDING_SECTIONS.map((sec, i) => ({
    ...sec,
    step: i + 1,
    status: sectionStatus(detail.completeness.items, sec.groups),
  }))
  const statusOf = (sid: SectionId) => sections.find((x) => x.id === sid)?.status ?? null
  const stepOf = (sid: SectionId) => sections.find((x) => x.id === sid)!.step
  const descOf = (sid: SectionId) => sections.find((x) => x.id === sid)!.description
  const req = (key: string) => editable && REQUIRED_FIELDS.has(key)

  // Jumping from the drawer closes it so the field is visible.
  const jump = (target: string) => {
    setPanelOpen(false)
    window.setTimeout(() => jumpTo(target), wide ? 0 : 250)
  }
  const sidePanel = (
    <>
      <PanelCard title="On this page">
        <SectionNav sections={sections} onJump={(sid) => jump(`section-${sid}`)} />
      </PanelCard>
      <PanelCard title={detail.completeness.ok ? 'Checklist: complete' : `Checklist: ${detail.completeness.error_count} missing`}>
        {dirty ? <p className="text-sm text-secondary">Saving… the checklist updates when your changes are saved.</p> : null}
        {detail.below_cost_acknowledged ? <Chip variant="warning">Below-cost prices acknowledged</Chip> : null}
        <OnboardingChecklist items={detail.completeness.items}
          onJump={(item) => jump(targetForChecklistKey(item.key, item.group))} />
      </PanelCard>
      <PanelCard title="What the documents say">
        <ExtractionPanel state={detail.extraction_state} results={detail.extraction_result} />
        {editable && detail.documents.intake.length > 0 && !['pending', 'running'].includes(detail.extraction_state) ? (
          <div>
            <Button variant="secondary" size="sm" loading={reextract.isPending}
              onClick={() => reextract.mutate(detail.id, {
                onSuccess: () => toast.success('Reading the documents again'),
                onError: fail('Could not start reading'),
              })}>
              <RotateCcw size={16} /> Read the documents again
            </Button>
          </div>
        ) : null}
      </PanelCard>
      <PanelCard title="History">
        <OnboardingHistory entries={detail.stage_history} />
      </PanelCard>
    </>
  )

  return (
    <div data-density="comfortable" data-surface="form" className="flex h-full flex-col">
      <PageHeaderBand
        title={detail.name || 'New product request'}
        description={detail.mode === 'update_existing'
          ? 'Correcting a product that already exists in Odoo. On approval the Odoo product is updated.'
          : 'Everything the warehouse needs before this product can be bought or received.'}
      >
        <div className="ml-auto flex items-center gap-2">
          <Chip variant="neutral">{detail.reference}</Chip>
          <Chip variant={STATUS_VARIANT[s]} dot>{STATUS_LABEL[s]}</Chip>
          {detail.odoo_product_tmpl_id ? (
            <Button variant="ghost" size="sm" onClick={() => window.open(odooProductTemplateUrl(detail.odoo_product_tmpl_id!), '_blank', 'noopener')}>
              <ExternalLink size={16} /> Odoo
            </Button>
          ) : null}
        </div>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/products', search: { tab: 'onboarding' } as never })}>
            <ArrowLeft size={16} /> New products
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          {editable ? <SaveStatusPill status={saveStatus} error={saveError} /> : null}
        </ToolbarRight>
      </Toolbar>

      <NextStepBar
        detail={detail}
        primary={primary}
        waiting={waiting}
        blocked={blocked}
        secondary={secondary}
        checklist={wide ? null : { missing: detail.completeness.error_count, onOpen: () => setPanelOpen(true) }}
      />

      <div className="min-h-0 flex-1 overflow-auto bg-canvas px-6 py-6">
        <div className="mx-auto flex w-full max-w-[calc(var(--form-content-width)+var(--form-aside-width)+1.5rem)] items-start gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-6">
            {s === 'draft' && detail.return_reason ? (
              <Banner tone="warning" title="Returned for changes">{detail.return_reason}</Banner>
            ) : null}
            {s === 'push_failed' && detail.push_error ? (
              <Banner tone="danger" title="Creating the product in Odoo failed">{detail.push_error}</Banner>
            ) : null}
            {s === 'cancelled' && detail.cancel_reason ? (
              <Banner tone="neutral" title="Cancelled">{detail.cancel_reason}</Banner>
            ) : null}
            {hasSignoff && editable ? (
              <Banner tone="neutral" title="Signed off">Changing anything now clears the sign-offs and sends it back to regulatory review.</Banner>
            ) : null}

            <StepSection id="identity" step={stepOf('identity')} title="Identity" description={descOf('identity')} status={statusOf('identity')}>
              <FormField id="name" label="Product name" required={req('name')}
                help={form.spi_branded ? 'SPI products start with "SPI-".' : 'Name it the way the team will recognise it on a drum label.'}>
                <Input value={form.name ?? ''} disabled={!editable} onChange={(e) => set('name', e.target.value || null)} />
              </FormField>
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField id="default_code" label="Internal reference (product code)" help="Optional.">
                  <Input value={form.default_code ?? ''} disabled={!editable} onChange={(e) => set('default_code', e.target.value || null)} />
                </FormField>
                <FormField id="categ" label="Category" help="Leave as All if you're not sure.">
                  <LookupPicker kind="categories" disabled={!editable}
                    value={form.categ_id ? [form.categ_id, form.categ_name ?? ''] : null}
                    onChange={(v) => setMany({ categ_id: v ? v[0] : null, categ_name: v ? v[1] : null })} />
                </FormField>
              </div>
              <div className="flex flex-col gap-3 rounded-md bg-surface-sunk px-4 py-3">
                <Checkbox label="SPI-branded product (SPI- name; ships with the SPI CofA)" checked={form.spi_branded}
                  disabled={!editable} onChange={(e) => set('spi_branded', e.target.checked)} />
                <div className="flex flex-wrap gap-6">
                  <Checkbox label="Can be sold" checked={form.sale_ok} disabled={!editable} onChange={(e) => set('sale_ok', e.target.checked)} />
                  <Checkbox label="Can be purchased" checked={form.purchase_ok} disabled={!editable} onChange={(e) => set('purchase_ok', e.target.checked)} />
                </div>
              </div>
              <FormField label="Sales description" help="Optional. Shown on quotes and sales orders.">
                <Textarea rows={3} value={form.description_sale ?? ''} disabled={!editable}
                  onChange={(e) => set('description_sale', e.target.value || null)} />
              </FormField>
            </StepSection>

            <StepSection id="units" step={stepOf('units')} title="Units & pack" description={descOf('units')} status={statusOf('units')}>
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField id="uom" label="Unit of measure (stock and sale)" required={req('uom')}>
                  <UomSelect value={form.uom_id} name={form.uom_name} disabled={!editable}
                    onChange={(id, name) => setMany({ uom_id: id, uom_name: name })} />
                </FormField>
                <FormField id="uom_po" label="Purchase unit of measure" required={req('uom_po')} help="The unit the vendor sells in.">
                  <UomSelect value={form.uom_po_id} name={form.uom_po_name} disabled={!editable}
                    onChange={(id, name) => setMany({ uom_po_id: id, uom_po_name: name })} />
                </FormField>
                <FormField id="pack_size" label={`Pack size${form.uom_po_name ? ` (${form.uom_po_name})` : ''}`} required={req('pack_size')}
                  help="How much is in one package.">
                  <Input inputMode="decimal" value={form.pack_size ?? ''} disabled={!editable} onChange={(e) => set('pack_size', e.target.value || null)} />
                </FormField>
                <FormField id="pack_description" label="Package" help="For example: 25 kg fiber drum.">
                  <Input value={form.pack_description ?? ''} disabled={!editable} onChange={(e) => set('pack_description', e.target.value || null)} />
                </FormField>
              </div>
            </StepSection>

            <StepSection id="vendor" step={stepOf('vendor')} title="Vendor" description={descOf('vendor')} status={statusOf('vendor')}>
              <VendorsEditor value={form.vendors} onChange={(v) => set('vendors', v)} disabled={!editable} purchaseUom={form.uom_po_name} />
            </StepSection>

            <StepSection id="pricing" step={stepOf('pricing')} title="Pricing" description={descOf('pricing')} status={statusOf('pricing')}>
              <PricingSummary preview={detail.pricing_preview} dirty={dirty} />
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField id="list_price" label={`List price${form.uom_name ? ` (per ${form.uom_name})` : ''}`}
                  help="Optional. Leave empty to use the public price.">
                  <Input inputMode="decimal" value={form.list_price ?? ''} disabled={!editable}
                    placeholder={detail.pricing_preview ? String(detail.pricing_preview.public_price) : 'cost + 30%'}
                    onChange={(e) => set('list_price', e.target.value || null)} />
                </FormField>
              </div>
              <div id="field-tiers" className="-m-1.5 flex flex-col gap-2 rounded-md p-1.5 transition-shadow data-[flash=true]:shadow-focus">
                <span className="text-md font-semibold text-primary">Quantity breaks on the Public Pricelist</span>
                <TiersEditor value={form.price_tiers} onChange={(v) => set('price_tiers', v)} disabled={!editable} uom={form.uom_name} />
              </div>
            </StepSection>

            <StepSection id="regulatory" step={stepOf('regulatory')} title="Regulatory data" description={descOf('regulatory')} status={statusOf('regulatory')}>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <FormField id="inci" label="INCI name(s)" required={req('inci')} help="One per line. A line break starts a new name.">
                    <Textarea rows={3} value={form.inci_names.join('\n')} disabled={!editable}
                      onChange={(e) => set('inci_names', e.target.value.split('\n'))} />
                  </FormField>
                  <ListPreview noun="INCI name" values={form.inci_names} editable={editable}
                    fromDoc={docList(detail.extraction_result, 'inci_names')} onUse={(v) => set('inci_names', v)} />
                </div>
                <div className="flex flex-col gap-2">
                  <FormField id="cas" label="CAS number(s)" help="One per line.">
                    <Textarea rows={3} value={form.cas_numbers.join('\n')} disabled={!editable}
                      onChange={(e) => set('cas_numbers', e.target.value.split('\n'))} />
                  </FormField>
                  <ListPreview noun="CAS number" values={form.cas_numbers} editable={editable}
                    fromDoc={docList(detail.extraction_result, 'cas_numbers')} onUse={(v) => set('cas_numbers', v)} />
                </div>
                <FormField id="country" label="Country of origin" required={req('country')}>
                  <CountryPicker variant="field" disabled={!editable}
                    value={form.country_of_origin_id ? [form.country_of_origin_id, form.country_of_origin_name ?? ''] : null}
                    onChange={(v) => setMany({ country_of_origin_id: v ? v[0] : null, country_of_origin_name: v ? v[1] : null })} />
                </FormField>
                <FormField id="hs_code" label="HS code" help="Optional for now; needed before it ships internationally.">
                  <Input value={form.hs_code ?? ''} disabled={!editable} onChange={(e) => set('hs_code', e.target.value || null)} />
                </FormField>
              </div>
              {editable ? (
                <HsCodeHelper intakeId={detail.id} current={form.hs_code} onUse={(code) => set('hs_code', code)} />
              ) : null}
              <ManufacturerBlock form={form} setMany={setMany} set={set} editable={editable}
                results={detail.extraction_result} />
              <div id="field-hazmat" className="flex flex-col gap-4 rounded-md bg-surface-sunk px-4 py-3 transition-shadow data-[flash=true]:shadow-focus">
                <Checkbox label="Hazardous material" checked={form.is_hazmat} disabled={!editable} onChange={(e) => set('is_hazmat', e.target.checked)} />
                {form.is_hazmat ? (
                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField label="UN number" required={editable}>
                      <Input value={form.hazmat_un_number ?? ''} disabled={!editable} onChange={(e) => set('hazmat_un_number', e.target.value || null)} />
                    </FormField>
                    <FormField label="Hazard class" required={editable}>
                      <Input value={form.hazmat_class_code ?? ''} disabled={!editable} onChange={(e) => set('hazmat_class_code', e.target.value || null)} />
                    </FormField>
                    <FormField label="Packing group">
                      <Select value={form.hazmat_packing_group ?? ''} disabled={!editable} onChange={(e) => set('hazmat_packing_group', e.target.value || null)}>
                        <option value="">Choose…</option><option value="I">I</option><option value="II">II</option><option value="III">III</option>
                      </Select>
                    </FormField>
                    <FormField label="Proper shipping name" required={editable}>
                      <Input value={form.hazmat_proper_shipping_name ?? ''} disabled={!editable} onChange={(e) => set('hazmat_proper_shipping_name', e.target.value || null)} />
                    </FormField>
                  </div>
                ) : null}
              </div>
            </StepSection>

            <StepSection id="documents" step={stepOf('documents')} title="Documents" description={descOf('documents')} status={statusOf('documents')}>
              <DocumentsSection detail={detail} editable={editable} />
            </StepSection>

            <StepSection id="notes" step={stepOf('notes')} title="Notes" description={descOf('notes')}>
              <Textarea aria-label="Notes" rows={4} value={form.notes ?? ''} disabled={!editable} onChange={(e) => set('notes', e.target.value || null)} />
            </StepSection>
          </div>

          {wide ? (
            <aside className="sticky top-0 flex max-h-[calc(100vh-14rem)] w-[var(--form-aside-width)] shrink-0 flex-col gap-4 overflow-auto">
              {sidePanel}
            </aside>
          ) : null}
        </div>
      </div>

      {!wide ? (
        <Drawer surface="form" open={panelOpen} onOpenChange={setPanelOpen} size="wide" title="Checklist and details">
          <div className="flex flex-col gap-4">{sidePanel}</div>
        </Drawer>
      ) : null}

      <Modal
        open={signOpen && signKind !== null}
        onOpenChange={(open) => !open && setSignOpen(false)}
        surface="form"
        tone="info"
        icon={CheckCircle2}
        title={signKind === 'regulatory' ? 'Give regulatory sign-off' : 'Give operations sign-off'}
        description={signKind === 'regulatory'
          ? 'Confirm the name, documents and regulatory data are right for this product.'
          : 'Confirm vendor, MOQ, cost, units, pack size and prices. Signing creates the product in Odoo.'}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setSignOpen(false)}>Back</Button>
            <Button variant="primary" loading={sign.isPending}
              disabled={signBlocked !== null || (signKind === 'commercial' && belowCost && !ackBelowCost)}
              onClick={doSign}>
              Sign off
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <FormField label="Note (optional)">
            <Textarea rows={2} value={signNote} onChange={(e) => setSignNote(e.target.value)} />
          </FormField>
          {signKind === 'commercial' && belowCost ? (
            <Checkbox label="Some prices are below vendor cost. I acknowledge this." checked={ackBelowCost}
              onChange={(e) => setAckBelowCost(e.target.checked)} />
          ) : null}
        </div>
      </Modal>

      <Modal
        open={dialog != null}
        onOpenChange={(open) => !open && setDialog(null)}
        surface="form"
        tone={dialog?.kind === 'cancel' ? 'danger' : 'warning'}
        icon={AlertTriangle}
        title={meta?.title ?? ''}
        description={meta?.description}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDialog(null)}>Back</Button>
            <Button
              variant={dialog?.kind === 'cancel' ? 'danger' : 'primary'}
              loading={busy}
              disabled={meta?.required && !dialogText.trim()}
              onClick={runDialog}
            >
              {meta?.action}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <FormField label={meta?.label ?? ''}>
            <Textarea rows={3} value={dialogText} onChange={(e) => setDialogText(e.target.value)} />
          </FormField>
        </div>
      </Modal>
    </div>
  )
}

/** What a one-per-line field will save, and the document's own list if it differs. Chris 2026-10-11:
 * "Acrylates/C10-30 Alkyl Acrylate Crosspolymer" pasted with a line break became two INCI names in Odoo. */
function ListPreview({ noun, values, fromDoc, editable, onUse }: {
  noun: string
  values: string[]
  fromDoc: { label: string; values: string[] } | null
  editable: boolean
  onUse: (values: string[]) => void
}) {
  const saved = cleanList(values)
  const differs = fromDoc && fromDoc.values.length > 0 && !sameList(saved, fromDoc.values)
  if (!saved.length && !differs) return null
  return (
    <div className="flex flex-col gap-2 text-sm">
      {saved.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-secondary">Saved as {saved.length} {noun}{saved.length === 1 ? '' : 's'}:</span>
          {saved.map((v) => <Chip key={v} variant="neutral" className="text-sm">{v}</Chip>)}
        </div>
      ) : null}
      {differs ? (
        <div className="flex flex-col gap-1.5 rounded-md bg-info-bg px-3 py-2 text-info-text">
          <div className="flex flex-wrap items-center gap-1.5">
            <span>The {fromDoc.label} lists:</span>
            {fromDoc.values.map((v) => <Chip key={v} variant="info" className="text-sm">{v}</Chip>)}
          </div>
          {editable ? (
            <div>
              <Button variant="secondary" size="sm" onClick={() => onUse(fromDoc.values)}>
                Use what the {fromDoc.label} says
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** Suggested HTS codes from Claude, each confirmed in the official schedule (hts.usitc.gov). */
function HsCodeHelper({ intakeId, current, onUse }: { intakeId: string; current: string | null; onUse: (code: string) => void }) {
  const suggest = useHsCodeSuggestions()
  const result = suggest.data
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border-default p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-primary">Look up the HS code</h3>
          <p className="text-sm text-secondary">
            Suggests US tariff codes from the INCI, CAS and documents, and checks each one against the official schedule
            (hts.usitc.gov). Nothing is saved until you choose one.
          </p>
        </div>
        <Button variant="secondary" size="sm" loading={suggest.isPending} onClick={() => suggest.mutate(intakeId)}>
          <Search size={16} /> {result ? 'Suggest again' : 'Suggest HS code'}
        </Button>
      </div>
      {suggest.isError ? <p className="text-md text-danger-text" role="alert">{(suggest.error as Error).message}</p> : null}
      {result?.error ? <p className="text-md text-danger-text" role="alert">{result.error}</p> : null}
      {result?.suggestions?.map((sug, i) => (
        <div key={sug.hts_code} className="flex flex-col gap-1.5 rounded-md bg-surface-sunk px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-lg font-semibold text-primary">{sug.hts_code}</span>
            {i === 0 ? <Chip variant="accent">Most likely</Chip> : null}
            {sug.general_duty ? <span className="text-sm text-secondary">General duty {sug.general_duty}</span> : null}
            <div className="ml-auto flex items-center gap-2">
              <a className="text-sm text-link hover:underline" href={sug.url} target="_blank" rel="noopener noreferrer">
                Official entry <ExternalLink size={12} className="inline" />
              </a>
              {current === sug.hts_code ? (
                <Chip variant="success" dot>In use</Chip>
              ) : (
                <Button variant="primary" size="sm" onClick={() => onUse(sug.hts_code)}>Use this code</Button>
              )}
            </div>
          </div>
          <div className="text-md text-primary">{sug.description}</div>
          <div className="text-sm text-secondary">{sug.reasoning}</div>
        </div>
      ))}
      {result?.suggestions && !result.suggestions.length && !result.error ? (
        <p className="text-md text-secondary">No suggestion matched a line in the official schedule. Look it up on hts.usitc.gov.</p>
      ) : null}
      {result?.caveat ? <p className="rounded-md bg-info-bg px-4 py-3 text-sm text-info-text">Check before using: {result.caveat}</p> : null}
    </div>
  )
}

/** kg / lb only for new products (Chris 2026-10-09). A different current unit
 * (correcting an existing product) stays selectable so it isn't silently lost. */
function UomSelect({ value, name, disabled, onChange }: {
  value: number | null
  name: string | null
  disabled: boolean
  onChange: (id: number | null, name: string | null) => void
}) {
  const { data } = useUomOptions()
  const options = data?.items ?? []
  const current = value != null && !options.some((o) => o.id === value)
    ? [{ id: value, name: name ?? String(value), label: `${name ?? value} (current)` }]
    : []
  return (
    <Select value={value ?? ''} disabled={disabled}
      onChange={(e) => {
        const id = e.target.value ? Number(e.target.value) : null
        const opt = [...options, ...current].find((o) => o.id === id)
        onChange(id, opt?.name ?? null)
      }}>
      <option value="">Choose…</option>
      {[...options, ...current].map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </Select>
  )
}

/** Who makes the product, as printed on the SDS; can be copied from what the SDS reading found. */
function ManufacturerBlock({ form, set, setMany, editable, results }: {
  form: OnboardingFields
  set: <K extends keyof OnboardingFields>(key: K, value: OnboardingFields[K]) => void
  setMany: (values: Partial<OnboardingFields>) => void
  editable: boolean
  results: OnboardingDetail['extraction_result']
}) {
  const facts = Object.entries(results ?? {})
    .sort(([a], [b]) => (a === 'sds' ? -1 : b === 'sds' ? 1 : 0))
    .map(([, f]) => f)
    .find((f) => !f.error && (f.manufacturer || f.manufacturer_address || f.emergency_phone))
  const fromDoc = facts ? {
    manufacturer_name: facts.manufacturer ?? null,
    manufacturer_address: facts.manufacturer_address ?? null,
    manufacturer_emergency_phone: facts.emergency_phone ?? null,
  } : null
  const differs = fromDoc && (Object.keys(fromDoc) as (keyof typeof fromDoc)[])
    .some((k) => fromDoc[k] && fromDoc[k] !== form[k])
  return (
    <div id="field-manufacturer" className="flex flex-col gap-5 rounded-lg border border-border-default p-4 transition-shadow data-[flash=true]:shadow-focus">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-primary">Manufacturer</h3>
          <p className="text-sm text-secondary">The company that makes the product, as on the SDS. Can differ from the vendor.</p>
        </div>
        {editable && differs ? (
          <Button variant="secondary" size="sm" title={facts?.doc_type_label ? `From the ${facts.doc_type_label}` : undefined}
            onClick={() => setMany(Object.fromEntries(Object.entries(fromDoc!).filter(([, v]) => v)) as Partial<OnboardingFields>)}>
            Use what the {facts?.doc_type_label ?? 'document'} says
          </Button>
        ) : null}
      </div>
      <FormField id="manufacturer_name" label="Manufacturer name">
        <Input value={form.manufacturer_name ?? ''} disabled={!editable} onChange={(e) => set('manufacturer_name', e.target.value || null)} />
      </FormField>
      <FormField label="Manufacturing site address">
        <Textarea rows={2} value={form.manufacturer_address ?? ''} disabled={!editable}
          onChange={(e) => set('manufacturer_address', e.target.value || null)} />
      </FormField>
      <FormField label="Emergency phone number" help="From SDS section 1. Saved on the Odoo product as the emergency contact." className="sm:max-w-[50%]">
        <Input value={form.manufacturer_emergency_phone ?? ''} disabled={!editable} inputMode="tel"
          onChange={(e) => set('manufacturer_emergency_phone', e.target.value || null)} />
      </FormField>
    </div>
  )
}

function money(n: number, ccy: string) {
  return n.toLocaleString('en-US', { style: 'currency', currency: ccy, maximumFractionDigits: 4 })
}

function PricingSummary({ preview, dirty }: { preview: OnboardingDetail['pricing_preview']; dirty: boolean }) {
  if (!preview) {
    return (
      <p className="rounded-md bg-surface-sunk px-4 py-3 text-md text-secondary">
        Enter the primary vendor's cost and currency and both units of measure to see the product cost and public price.
      </p>
    )
  }
  return (
    <div className="grid gap-4 rounded-md border border-border-subtle bg-surface-sunk p-4 sm:grid-cols-2">
      <div>
        <div className="text-sm text-secondary">Product cost (from the primary vendor)</div>
        <div className="text-xl font-semibold text-primary">{money(preview.unit_cost, preview.currency)} <span className="text-md font-normal text-secondary">per {preview.uom}</span></div>
        {preview.vendor_currency !== preview.currency ? (
          <div className="text-sm text-secondary">Converted from {preview.vendor_currency} at Odoo's current rate</div>
        ) : null}
      </div>
      <div>
        <div className="text-sm text-secondary">Public price (cost + {preview.markup_pct}%)</div>
        <div className="text-xl font-semibold text-primary">{money(preview.public_price, preview.currency)} <span className="text-md font-normal text-secondary">per {preview.uom}</span></div>
        <div className="text-sm text-secondary">Set automatically on the Public Pricelist{dirty ? ' · updates when saved' : ''}</div>
      </div>
    </div>
  )
}

function Banner({ tone, title, children }: { tone: 'warning' | 'danger' | 'neutral'; title: string; children: React.ReactNode }) {
  const cls = {
    warning: 'border-warning-border bg-warning-bg text-warning-text',
    danger: 'border-danger-border bg-danger-bg text-danger-text',
    neutral: 'border-border-default bg-surface text-secondary',
  }[tone]
  return (
    <div role="alert" className={`rounded-lg border px-5 py-4 ${cls}`}>
      <div className="text-lg font-semibold">{title}</div>
      <div className="text-md">{children}</div>
    </div>
  )
}

function DocumentsSection({ detail, editable }: { detail: OnboardingDetail; editable: boolean }) {
  const { data: types = [] } = useDocumentTypes()
  const upload = useUploadOnboardingDocument()
  const archive = useArchiveOnboardingDocument()
  const [typeKey, setTypeKey] = useState('')
  const [error, setError] = useState<string | null>(null)

  const all = [...detail.documents.intake, ...detail.documents.on_product]
  const chips = documentChips(types, all, detail.spi_branded)

  const send = (file: File) => {
    setError(null)
    if (!typeKey) return setError('Choose the document type first.')
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') return setError('Please choose a PDF file.')
    upload.mutate(
      { id: detail.id, file, docTypeKey: typeKey },
      {
        onSuccess: () => {
          toast.success('Document uploaded')
          setTypeKey('')
        },
        onError: (err) => setError((err as Error).message),
      },
    )
  }

  return (
    <>
      <div className="flex flex-wrap gap-2" aria-label="Document status">
        {chips.map((c) => (
          <Chip key={c.key} variant={c.state === 'done' ? 'success' : 'warning'} dot className="text-sm">
            {c.label}{c.state === 'missing' ? ' (required)' : ''}
          </Chip>
        ))}
      </div>

      {all.length ? (
        <div className="overflow-hidden rounded-lg border border-border-default">
          <table className="w-full border-collapse text-md">
            <thead className="bg-surface-sunk text-left text-sm font-semibold text-secondary">
              <tr>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">File</th>
                <th className="px-4 py-2.5">Uploaded</th>
                <th className="px-4 py-2.5"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {detail.documents.intake.map((d) => (
                <tr key={d.id} className="border-t border-border-subtle">
                  <td className="px-4 py-3 font-medium text-primary">{d.doc_type_label}</td>
                  <td className="px-4 py-3">
                    <a className="inline-flex items-center gap-1.5 text-link hover:underline" href={documentUrl(d.document_id, d.filename)} target="_blank" rel="noopener noreferrer">
                      <FileText size={16} aria-hidden="true" /> {d.filename}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-secondary">{d.uploaded_by ?? ''}{d.created_at ? <div className="text-sm">{formatDate(d.created_at)}</div> : null}</td>
                  <td className="px-4 py-3 text-right">
                    {editable ? (
                      <Button variant="ghost" size="sm"
                        onClick={() => archive.mutate({ id: detail.id, docId: d.id }, { onError: (e) => toast.danger('Withdraw failed', { description: (e as Error).message }) })}>
                        Withdraw
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {detail.documents.on_product.map((d) => (
                <tr key={`p${d.id}`} className="border-t border-border-subtle">
                  <td className="px-4 py-3 font-medium text-primary">{d.doc_type_label}</td>
                  <td className="px-4 py-3">
                    <a className="inline-flex items-center gap-1.5 text-link hover:underline" href={documentUrl(d.document_id, d.filename)} target="_blank" rel="noopener noreferrer">
                      <FileText size={16} aria-hidden="true" /> {d.filename}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-secondary">{formatDate(d.created_at)}</td>
                  <td className="px-4 py-3 text-right"><Chip variant="neutral">Already on the product</Chip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-md text-secondary">No documents yet.</p>
      )}

      {editable ? (
        <div className="flex flex-col gap-4 rounded-lg border border-border-default p-4">
          <h3 className="text-lg font-semibold text-primary">Add a document</h3>
          <FormField label="Document type" className="sm:max-w-[50%]">
            <Select aria-label="Document type" value={typeKey} onChange={(e) => { setTypeKey(e.target.value); setError(null) }}>
              <option value="">Choose…</option>
              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </Select>
          </FormField>
          <PdfDropZone label="Document PDF" busy={upload.isPending} disabled={!typeKey} onFile={send}
            hint={typeKey ? 'PDF only. It uploads as soon as you choose it.' : 'Choose the document type first.'} />
          {error ? <p className="text-md font-medium text-danger-text" role="alert">{error}</p> : null}
        </div>
      ) : null}
    </>
  )
}
